/**
 * ===================================================
 * Module: frontend.src.components.PDFEditor.tsx
 * Description: PDF Editor component for viewing and editing redaction boxes
 * Owner: Kawada Technosystem Co Ltd
 * All Rights Reserved
 * This script is proprietary and confidential.
 * Unauthorized copying, distribution, or modification of this
 * file, via any medium, is strictly prohibited.
 * ===================================================
 */

import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { apiService } from '../services/api';
import { RedactionBox, PageMetadata, RedactionBoxesResponse } from '../types';
import PDFViewer from './PDFViewer';
import NotificationSystem, { Notification } from './NotificationSystem';
import * as pdfjsLib from 'pdfjs-dist';
import '../styles/PDFEditor.css';
import '../styles/NotificationSystem.css';
import PDFPageNavigator from './PDFPageNavigator';
import { loadPdfDocument } from '../utils/pdfConfig';

// Worker is configured in pdfConfig.ts - this is a backup in case it's not loaded yet
pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://unpkg.com/pdfjs-dist@3.4.120/build/pdf.worker.min.js';

const PDFEditor: React.FC = () => {
  const { t } = useTranslation('pdfEditor');
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();

  // State
  const [pdfDoc, setPdfDoc] = useState<any>(null);
  const [totalPages, setTotalPages] = useState(0);
  const [pageNum, setPageNum] = useState(1);
  const [scale, setScale] = useState(1.44);
  const [isDrawing, setIsDrawing] = useState(false);
  const [isDrawMode, setIsDrawMode] = useState(false);
  const [redactionBoxes, setRedactionBoxes] = useState<Record<string, RedactionBox>>({});
  const [pageMetadata, setPageMetadata] = useState<Record<string, PageMetadata>>({});
  const [selectedBoxIds, setSelectedBoxIds] = useState<Set<string>>(new Set());
  const [isLoading, setIsLoading] = useState(true);
  const [isRendering, setIsRendering] = useState(false);
  const [renderedPages, setRenderedPages] = useState<Set<number>>(new Set());
  const [isNavigatorOpen, setIsNavigatorOpen] = useState(true);
  const [showInstructions, setShowInstructions] = useState(false);
  const [showRedactionModeDropdown, setShowRedactionModeDropdown] = useState(false);
  const [dropdownPos, setDropdownPos] = useState<'left' | 'right'>('left');
  // Toolbar position — stored as percentages for zoom-resilience, but rendered as pixels
  const toolbarRef = useRef<HTMLDivElement>(null);
  const [toolbarPos, setToolbarPos] = useState<{ x: number; y: number }>(() => {
    try {
      const saved = localStorage.getItem('pdfEditorToolbarPos_v2');
      if (saved) {
        const { xPct, yPct } = JSON.parse(saved);
        // Convert saved percentages back to pixels using visual viewport
        const vw = (window.visualViewport?.width ?? window.innerWidth);
        const vh = (window.visualViewport?.height ?? window.innerHeight);
        return { x: xPct * vw, y: yPct * vh };
      }
    } catch { /* ignore */ }
    const vw = (window.visualViewport?.width ?? window.innerWidth);
    return { x: vw - 110, y: 100 };
  });
  const [isDraggingToolbar, setIsDraggingToolbar] = useState(false);
  const [toolbarDragStart, setToolbarDragStart] = useState({ x: 0, y: 0 });

  // Add notifications state
  const [notifications, setNotifications] = useState<Notification[]>([]);

  // Notification management functions
  const addNotification = (notification: Omit<Notification, 'id'>) => {
    const id = Date.now().toString();
    setNotifications(prev => [...prev, { ...notification, id }]);
    return id;
  };

  const removeNotification = (id: string) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  // Check if we have any boxes to apply
  const hasBoxes = Object.keys(redactionBoxes).length > 0;

  // Load PDF on component mount
  useEffect(() => {
    if (sessionId) {
      loadPDF();
    }
  }, [sessionId]);

  // Keyboard event handler for Delete/Backspace
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Check if we're in an input field - don't handle delete there
      const activeElement = document.activeElement;
      if (activeElement && (activeElement.tagName === 'INPUT' || activeElement.tagName === 'TEXTAREA')) {
        return;
      }

      // Handle Delete or Backspace key
      if ((e.key === 'Delete' || e.key === 'Backspace') && selectedBoxIds.size > 0 && !isDrawMode) {
        e.preventDefault();
        // Delete all selected boxes
        setRedactionBoxes(prev => {
          const newBoxes = { ...prev };
          selectedBoxIds.forEach(boxId => {
            delete newBoxes[boxId];
          });
          return newBoxes;
        });
        setSelectedBoxIds(new Set());
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [selectedBoxIds, isDrawMode]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('.dropdown-container')) {
        setShowRedactionModeDropdown(false);
      }
    };

    if (showRedactionModeDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showRedactionModeDropdown]);

  // Handle dynamic dropdown positioning
  useEffect(() => {
    if (showRedactionModeDropdown) {
      const dropdownWidth = 220; // Estimate
      const toolbarWidth = 84;   // Estimate (actual toolbar width is around 84px)

      const spaceLeft = toolbarPos.x;
      const spaceRight = window.innerWidth - (toolbarPos.x + toolbarWidth);

      if (spaceLeft < dropdownWidth) {
        setDropdownPos('right');
      } else if (spaceRight < dropdownWidth) {
        setDropdownPos('left');
      } else {
        // If x < y, open left (min logic)
        setDropdownPos(spaceLeft < spaceRight ? 'left' : 'right');
      }
    }
  }, [showRedactionModeDropdown, toolbarPos.x]);

  // Reset scroll on page change
  useEffect(() => {
    const container = document.querySelector('.pdf-viewer-container');
    if (container) {
      container.scrollTop = 0;
    }
  }, [pageNum]);

  // Helper: get viewport dimensions that work correctly under browser zoom
  const getViewport = useCallback(() => {
    const vp = window.visualViewport;
    return {
      w: vp ? vp.width : window.innerWidth,
      h: vp ? vp.height : window.innerHeight,
    };
  }, []);

  // Helper: get actual toolbar element size
  const getToolbarSize = useCallback(() => {
    if (toolbarRef.current) {
      const rect = toolbarRef.current.getBoundingClientRect();
      return { w: rect.width, h: rect.height };
    }
    return { w: 90, h: 500 }; // fallback estimate
  }, []);

  // Helper: clamp toolbar position within viewport
  const clampToViewport = useCallback((x: number, y: number) => {
    const vp = getViewport();
    const tb = getToolbarSize();
    const pad = 8; // keep toolbar 8px from viewport edges
    const clamp = (v: number, min: number, max: number) => Math.min(Math.max(v, min), max);
    return {
      x: clamp(x, pad, vp.w - tb.w - pad),
      y: clamp(y, pad, vp.h - tb.h - pad),
    };
  }, [getViewport, getToolbarSize]);

  // Persist toolbar position as percentages whenever it changes (not during drag for perf)
  const saveToolbarPos = useCallback((pos: { x: number; y: number }) => {
    try {
      const vp = getViewport();
      localStorage.setItem('pdfEditorToolbarPos_v2', JSON.stringify({
        xPct: vp.w > 0 ? pos.x / vp.w : 0.9,
        yPct: vp.h > 0 ? pos.y / vp.h : 0.1,
      }));
    } catch { /* ignore */ }
  }, [getViewport]);

  // Handle toolbar dragging
  useEffect(() => {
    const handleGlobalMouseMove = (e: MouseEvent) => {
      if (isDraggingToolbar) {
        const clamped = clampToViewport(
          e.clientX - toolbarDragStart.x,
          e.clientY - toolbarDragStart.y
        );
        setToolbarPos(clamped);
      }
    };

    const handleGlobalMouseUp = () => {
      if (isDraggingToolbar) {
        setIsDraggingToolbar(false);
        // Save position on drag end
        setToolbarPos(prev => {
          saveToolbarPos(prev);
          return prev;
        });
      }
    };

    if (isDraggingToolbar) {
      window.addEventListener('mousemove', handleGlobalMouseMove);
      window.addEventListener('mouseup', handleGlobalMouseUp);
    }

    return () => {
      window.removeEventListener('mousemove', handleGlobalMouseMove);
      window.removeEventListener('mouseup', handleGlobalMouseUp);
    };
  }, [isDraggingToolbar, toolbarDragStart, clampToViewport, saveToolbarPos]);

  // Re-clamp toolbar on window resize AND browser zoom (visualViewport.resize)
  useEffect(() => {
    const handleViewportChange = () => {
      setToolbarPos(prev => {
        // Restore from saved percentages for zoom-resilience
        try {
          const saved = localStorage.getItem('pdfEditorToolbarPos_v2');
          if (saved) {
            const { xPct, yPct } = JSON.parse(saved);
            const vp = getViewport();
            const newPos = clampToViewport(xPct * vp.w, yPct * vp.h);
            return newPos;
          }
        } catch { /* ignore */ }
        return clampToViewport(prev.x, prev.y);
      });
    };

    window.addEventListener('resize', handleViewportChange);
    // visualViewport fires on browser zoom (Ctrl+scroll)
    window.visualViewport?.addEventListener('resize', handleViewportChange);

    return () => {
      window.removeEventListener('resize', handleViewportChange);
      window.visualViewport?.removeEventListener('resize', handleViewportChange);
    };
  }, [getViewport, clampToViewport]);

  // Cleanup PDF document on unmount or before loading new one
  useEffect(() => {
    return () => {
      if (pdfDoc) {
        pdfDoc.destroy().catch(() => { });
      }
    };
  }, [pdfDoc]);

  const loadPDF = async () => {
    if (!sessionId) return;

    try {
      setIsLoading(true);

      // Explicitly destroy previous document if it exists to free memory
      if (pdfDoc) {
        await pdfDoc.destroy().catch(() => { });
        setPdfDoc(null);
      }

      // Instead of downloading the whole Blob, get the presigned URL
      // PDF.js will use HTTP Range requests to load only needed pages
      const pdfUrl = await apiService.getSessionInputPdfUrl(sessionId);
      const pdf = await loadPdfDocument(pdfUrl);

      setPdfDoc(pdf);
      setTotalPages(pdf.numPages);

      await loadExistingRedactionBoxes();

      await waitForPageNavigatorReady();

      setIsLoading(false);
    } catch (error) {
      addNotification({
        type: 'error',
        message: t('notifications.loadPdfFailed')
      });
      setIsLoading(false);
    }
  };

  const waitForPageNavigatorReady = async () => {
    await new Promise(resolve => setTimeout(resolve, 1000));
  };

  // Load existing redaction boxes from session
  const loadExistingRedactionBoxes = async () => {
    if (!sessionId) return;

    try {
      const response = await apiService.getSessionRedactionBoxes(sessionId);

      // Handle new format with file_metadata and detections
      if (response && typeof response === 'object' && 'file_metadata' in response && 'detections' in response) {
        const data = response as RedactionBoxesResponse;

        // Store page metadata (for reference, though rotation transforms
        // now read from PDF.js page.rotate directly)
        if (data.file_metadata?.pages) {
          setPageMetadata(data.file_metadata.pages);
        }

        // Parse detections — coords are always in MediaBox (unrotated) space.
        // PDFViewer rotation transforms handle display conversion.
        const parsedBoxes: Record<string, RedactionBox> = {};
        if (Array.isArray(data.detections)) {
          data.detections.forEach((box: any, index: number) => {
            const boxId = `box_${index}`;
            parsedBoxes[boxId] = {
              bbox: box.bbox,
              page: box.page
            };
          });
        }
        setRedactionBoxes(parsedBoxes);
      }
      // Fallback: handle legacy flat array format (user-persisted)
      // Coordinates are in MediaBox (unrotated) space — same as new format.
      // No migration needed; PDFViewer rotation transforms handle display.
      else if (response && Array.isArray(response)) {
        const parsedBoxes: Record<string, RedactionBox> = {};
        response.forEach((box: any, index: number) => {
          const boxId = `box_${index}`;
          parsedBoxes[boxId] = {
            bbox: box.bbox,
            page: box.page
          };
        });
        setRedactionBoxes(parsedBoxes);
      }
    } catch (error) {
      addNotification({
        type: 'error',
        message: t('notifications.loadBoxesFailed')
      });
    }
  };

  useEffect(() => {
    if (!isLoading && pdfDoc) {
      const timer = setTimeout(() => {
        setRedactionBoxes(prev => ({ ...prev }));
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [isLoading, pdfDoc]);

  const [pageInput, setPageInput] = useState(pageNum.toString());

  // Zoom input state
  const [zoomInput, setZoomInput] = useState(Math.round(scale * 100).toString());

  // Sync zoomInput with scale when scale changes (e.g. via buttons)
  useEffect(() => {
    setZoomInput(Math.round(scale * 100).toString());
  }, [scale]);

  const handleZoomInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setZoomInput(e.target.value);
  }, []);

  const handleZoomInputBlur = useCallback(() => {
    const value = parseInt(zoomInput);
    if (isNaN(value) || value < 100 || value > 300) {
      setZoomInput(Math.round(scale * 100).toString());
    } else {
      setScale(value / 100);
    }
  }, [zoomInput, scale]);

  const handleZoomInputKeyPress = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      let value = parseInt(zoomInput);
      if (isNaN(value)) {
        setZoomInput(Math.round(scale * 100).toString());
      } else {
        if (value < 100) value = 100;
        if (value > 300) value = 300;
        setScale(value / 100);
        setZoomInput(value.toString());
      }
      (e.target as HTMLInputElement).blur();
    }
  }, [zoomInput, scale]);

  // Sync pageInput with pageNum when pageNum changes externally
  useEffect(() => {
    setPageInput(pageNum.toString());
  }, [pageNum]);

  // Navigation functions
  const nextPage = useCallback(() => {
    if (pageNum < totalPages) {
      const newPage = pageNum + 1;
      setPageNum(newPage);
    }
  }, [pageNum, totalPages]);

  const previousPage = useCallback(() => {
    if (pageNum > 1) {
      const newPage = pageNum - 1;
      setPageNum(newPage);
    }
  }, [pageNum]);

  // Handle page input change - allow any input
  const handlePageInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setPageInput(e.target.value);
  }, []);

  // Handle page input blur (when user clicks away)
  const handlePageInputBlur = useCallback(() => {
    const value = parseInt(pageInput);
    if (isNaN(value) || value < 1 || value > totalPages) {
      setPageInput(pageNum.toString());
    } else {
      if (value !== pageNum) {
        setPageNum(value);
      }
    }
  }, [pageInput, pageNum, totalPages]);

  // Handle page input key press
  const handlePageInputKeyPress = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      let value = parseInt(pageInput);

      if (isNaN(value)) {
        // If invalid, revert
        setPageInput(pageNum.toString());
      } else {
        // Clamp value
        if (value < 1) value = 1;
        if (value > totalPages) value = totalPages;

        setPageNum(value);
        setPageInput(value.toString());
      }

      (e.target as HTMLInputElement).blur();
    }
  }, [pageInput, pageNum, totalPages]);

  // Add debouncing for zoom functions
  const zoomIn = useCallback(() => {
    setScale(prev => {
      const newScale = Math.min(prev * 1.2, 3.0);
      return newScale;
    });
  }, []);

  const zoomOut = useCallback(() => {
    setScale(prev => {
      const newScale = Math.max(prev / 1.2, 1.0);
      return newScale;
    });
  }, []);

  // Box management functions
  const handleBoxCreate = (box: RedactionBox) => {
    const boxId = `box_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    setRedactionBoxes(prev => ({ ...prev, [boxId]: box }));
    setSelectedBoxIds(new Set([boxId]));
  };

  const handleBoxSelect = (boxId: string, ctrlKey: boolean) => {
    if (boxId === '') {
      // Deselect all
      setSelectedBoxIds(new Set());
    } else if (ctrlKey) {
      // Toggle selection with ctrl key
      setSelectedBoxIds(prev => {
        const newSet = new Set(prev);
        if (newSet.has(boxId)) {
          newSet.delete(boxId);
        } else {
          newSet.add(boxId);
        }
        return newSet;
      });
    } else {
      // Single select without ctrl
      setSelectedBoxIds(new Set([boxId]));
    }
  };

  const handleBoxDelete = (boxId: string) => {
    setRedactionBoxes(prev => {
      const newBoxes = { ...prev };
      delete newBoxes[boxId];
      return newBoxes;
    });
    setSelectedBoxIds(prev => {
      const newSet = new Set(prev);
      newSet.delete(boxId);
      return newSet;
    });
  };

  // Add this new function to handle box updates
  const handleBoxUpdate = (boxId: string, updatedBox: RedactionBox) => {
    setRedactionBoxes(prev => ({
      ...prev,
      [boxId]: updatedBox
    }));
  };

  const deleteSelectedBoxes = () => {
    if (selectedBoxIds.size > 0) {
      setRedactionBoxes(prev => {
        const newBoxes = { ...prev };
        selectedBoxIds.forEach(boxId => {
          delete newBoxes[boxId];
        });
        return newBoxes;
      });
      setSelectedBoxIds(new Set());
    }
  };

  const generateFinalPDF = async (mode: 'black' | 'transparent' = 'black') => {
    if (!sessionId) return;

    // Close the dropdown
    setShowRedactionModeDropdown(false);

    try {
      addNotification({
        type: 'progress',
        message: t('notifications.generatingPdf'),
        persistent: true
      });

      // Convert redaction boxes to backend format with coord_version
      const backendBoxes = Object.values(redactionBoxes).map(box => ({
        bbox: box.bbox,
        page: box.page
      }));

      // Send versioned data so the worker knows coords are in rotated display space
      const versionedBoxes: any = {
        file_metadata: {
          pages: pageMetadata,
          total_pages: totalPages,
          dpi: 72
        },
        detections: backendBoxes,
        coord_version: 2
      };

      await apiService.applyRedactions(sessionId, versionedBoxes as any, mode);

      // Poll for completion
      let attempts = 0;
      const maxAttempts = 300; // 300 * 2s = 600s = 10 minutes timeout
      const pollInterval = 2000;

      while (attempts < maxAttempts) {
        // Wait first to give backend time to start
        await new Promise(resolve => setTimeout(resolve, pollInterval));

        const exists = await apiService.checkRedactedPdfExists(sessionId, mode);
        if (exists) {
          break;
        }
        attempts++;
      }

      if (attempts >= maxAttempts) {
        throw new Error(t('notifications.pdfGenerationTimeout'));
      }

      // Update notification to indicate downloading
      setNotifications(prev => prev.filter(n => n.type !== 'progress' || n.message !== t('notifications.generatingPdf')));
      addNotification({
        type: 'progress',
        message: t('details.downloading'),
        persistent: true
      });

      // Trigger download
      await apiService.downloadRedactedPdf(sessionId, mode);

      setNotifications(prev => prev.filter(n => n.type !== 'progress' || n.message !== t('details.downloading')));
      addNotification({
        type: 'success',
        message: t('details.downloadStarted')
      });

    } catch (error: any) {
      setNotifications(prev => prev.filter(n => n.type !== 'progress'));
      addNotification({
        type: 'error',
        message: error.message || t('notifications.pdfFailed')
      });
    }
  };

  // Save changes to session
  const saveChangesToSession = async () => {
    if (!sessionId) return;

    const confirmed = window.confirm(
      t('confirmations.saveChanges')
    );

    if (!confirmed) return;

    try {
      addNotification({
        type: 'progress',
        message: t('notifications.saving'),
        persistent: true
      });

      const backendBoxes = Object.values(redactionBoxes).map(box => ({
        bbox: box.bbox,
        page: box.page
      }));

      // Save in versioned format with coord_version: 2 and file_metadata
      // so that future loads know no migration is needed.
      const versionedData: any = {
        file_metadata: {
          pages: pageMetadata,
          total_pages: totalPages,
          dpi: 72
        },
        detections: backendBoxes,
        coord_version: 2
      };

      await apiService.persistRedactions(sessionId, versionedData);

      setNotifications(prev => prev.filter(n => n.type !== 'progress' || n.message !== t('notifications.saving')));
      addNotification({
        type: 'success',
        message: t('notifications.saved')
      });
    } catch (error) {
      setNotifications(prev => prev.filter(n => n.type !== 'progress' || n.message !== t('notifications.saving')));
      addNotification({
        type: 'error',
        message: t('notifications.saveFailed')
      });
    }
  };

  // Draw mode functions
  const toggleDrawMode = () => {
    setIsDrawMode(!isDrawMode);
    if (isDrawMode) {
      setSelectedBoxIds(new Set());
    }
  };

  const enterSelectMode = useCallback(() => {
    if (isDrawMode) {
      setIsDrawMode(false);
    }
    setSelectedBoxIds(new Set());
  }, [isDrawMode]);



  // Right-click handler to enter select mode
  const handleContextMenu = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    if (isDrawMode) {
      enterSelectMode();
    }
  }, [isDrawMode, enterSelectMode]);

  // Event handlers for PDF viewer
  const handleMouseDown = (x: number, y: number) => {
    if (isDrawMode) {
      setIsDrawing(true);
    }
  };

  const handleMouseMove = (x: number, y: number) => {
  };

  const handleMouseUp = (x: number, y: number) => {
    if (isDrawing && isDrawMode) {
      setIsDrawing(false);
    }
  };


  const handlePageRender = (pageNumber: number) => {
    // Track which pages have been rendered by the main viewer.
    // The thumbnail navigator uses this to know when it's safe to generate
    // thumbnails (rendering the same page concurrently corrupts PDF.js
    // internal state for PDFs with JPEG MCU errors on some platforms).
    setRenderedPages(prev => {
      if (prev.has(pageNumber)) return prev;
      const next = new Set(prev);
      next.add(pageNumber);
      return next;
    });
  };

  // Handle page selection from navigator - add debouncing
  const handlePageSelect = useCallback((pageNumber: number) => {
    if (pageNumber >= 1 && pageNumber <= totalPages && pageNumber !== pageNum) {
      setPageNum(pageNumber);
    }
  }, [pageNum, totalPages]);

  // Toggle navigator
  const toggleNavigator = () => {
    setIsNavigatorOpen(!isNavigatorOpen);
  };

  // Toggle instructions
  const toggleInstructions = () => {
    setShowInstructions(!showInstructions);
  };

  return (
    <div className="pdf-editor">
      {isLoading && (
        <div className="loading-overlay">
          <div className="loading-spinner"></div>
          <div className="loading-text">{t('loading.pdf')}</div>
        </div>
      )}

      {showInstructions && (
        <div className="instructions-overlay" onClick={() => setShowInstructions(false)}>
          <div className="instructions-modal" onClick={(e) => e.stopPropagation()}>
            <div className="instructions-header">
              <h2>{t('instructions.title')}</h2>
              <button className="close-button" onClick={() => setShowInstructions(false)}>×</button>
            </div>
            <div className="instructions-content">
              <h3>{t('instructions.drawing.title')}</h3>
              <ol>
                <li style={{ whiteSpace: 'pre-line' }}>{t('instructions.drawing.step1')}</li>
                <li style={{ whiteSpace: 'pre-line' }}>{t('instructions.drawing.step2')}</li>
              </ol>

              <h3>{t('instructions.editing.title')}</h3>
              <ul>
                <li style={{ whiteSpace: 'pre-line' }}>{t('instructions.editing.select')}</li>
                <li style={{ whiteSpace: 'pre-line' }}>{t('instructions.editing.clickToSelect')}</li>
                <li style={{ whiteSpace: 'pre-line' }}>{t('instructions.editing.drag')}</li>
                <li style={{ whiteSpace: 'pre-line' }}>{t('instructions.editing.resize')}</li>
                <li style={{ whiteSpace: 'pre-line' }}>{t('instructions.editing.deleteButton')}</li>
              </ul>

              <h3>{t('instructions.navigation.title')}</h3>
              <ul>
                <li style={{ whiteSpace: 'pre-line' }}>{t('instructions.navigation.arrows')}</li>
                <li style={{ whiteSpace: 'pre-line' }}>{t('instructions.navigation.zoom')}</li>
                <li style={{ whiteSpace: 'pre-line' }}>{t('instructions.navigation.pan')}</li>
                <li style={{ whiteSpace: 'pre-line' }}>{t('instructions.navigation.tip')}</li>
              </ul>

              <h3>{t('instructions.saving.title')}</h3>
              <ul>
                <li style={{ whiteSpace: 'pre-line' }}>{t('instructions.saving.save')}</li>
                <li style={{ whiteSpace: 'pre-line' }}>{t('instructions.saving.download')}</li>
              </ul>

              <div className="instruction-tip">
                {t('instructions.saving.tip')}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="pdf-editor-content">
        <div className="pdf-viewer-container" onContextMenu={handleContextMenu}>
          <PDFViewer
            pdfDoc={pdfDoc}
            pageNum={pageNum}
            scale={scale}
            redactionBoxes={redactionBoxes}
            pageMetadata={pageMetadata}
            isDrawing={isDrawing}
            isDrawMode={isDrawMode}
            selectedBoxIds={selectedBoxIds}
            onPageRender={handlePageRender}
            onBoxSelect={handleBoxSelect}
            onBoxCreate={handleBoxCreate}
            onBoxDelete={handleBoxDelete}
            onBoxUpdate={handleBoxUpdate}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onError={(message) => addNotification({ type: 'error', message })}
          />

          {isRendering && (
            <div className="rendering-overlay">
              <div className="rendering-spinner"></div>
              <div className="rendering-text">{t('loading.rendering')}</div>
            </div>
          )}
        </div>

        <div className={`pdf-navigator-slideout ${isNavigatorOpen ? 'open' : ''}`}>
          <div className="navigator-header">
            <h3>{t('page.pages')}</h3>
            <div className="page-count">{t('page.pageCount', { count: totalPages })}</div>
          </div>
          <PDFPageNavigator
            pdfDoc={pdfDoc}
            currentPage={pageNum}
            onPageSelect={handlePageSelect}
            renderedPages={renderedPages}
            onError={(message) => addNotification({ type: 'error', message })}
          />
        </div>

        <button
          className={`navigator-toggle ${isNavigatorOpen ? 'open' : ''}`}
          onClick={toggleNavigator}
          title={isNavigatorOpen ? t('toolbar.hideNavigator') : t('toolbar.showNavigator')}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            {isNavigatorOpen ? (
              <path d="M15 18l-6-6 6-6" />
            ) : (
              <path d="M9 18l6-6-6-6" />
            )}
          </svg>
        </button>

        <div
          ref={toolbarRef}
          className="floating-toolbar"
          style={{
            left: `${toolbarPos.x}px`,
            top: `${toolbarPos.y}px`,
            position: 'fixed'
          }}
          onMouseDown={(e) => {
            if ((e.target as HTMLElement).closest('.toolbar-button')) return;
            setIsDraggingToolbar(true);
            const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
            setToolbarDragStart({
              x: e.clientX - rect.left,
              y: e.clientY - rect.top
            });
          }}
        >
          <div className="toolbar-drag-handle">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="9" cy="5" r="1" />
              <circle cx="9" cy="12" r="1" />
              <circle cx="9" cy="19" r="1" />
              <circle cx="15" cy="5" r="1" />
              <circle cx="15" cy="12" r="1" />
              <circle cx="15" cy="19" r="1" />
            </svg>
          </div>
          <div className="toolbar-group">
            <button
              className={`toolbar-button ${!isDrawMode ? 'active' : ''}`}
              onClick={enterSelectMode}
              title={t('tooltips.select')}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M3 3l7 7m0 0v-6m0 6h-6"></path>
                <path d="M13 13l6 6"></path>
              </svg>
              <span>{t('toolbar.select')}</span>
            </button>

            <button
              className={`toolbar-button ${isDrawMode ? 'active' : ''}`}
              onClick={toggleDrawMode}
              title={isDrawMode ? t('tooltips.exitDrawMode') : t('tooltips.enterDrawMode')}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2" strokeDasharray="4 4" />
              </svg>
              <span>{t('toolbar.draw')}</span>
            </button>

            <button
              className="toolbar-button"
              onClick={deleteSelectedBoxes}
              disabled={selectedBoxIds.size === 0 || isDrawMode}
              title={selectedBoxIds.size > 1 ? t('tooltips.deleteMultiple', { count: selectedBoxIds.size }) : t('tooltips.delete')}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                <line x1="10" y1="11" x2="10" y2="17"></line>
                <line x1="14" y1="11" x2="14" y2="17"></line>
              </svg>
              <span>{selectedBoxIds.size > 1 ? t('toolbar.deleteMultiple', { count: selectedBoxIds.size }) : t('toolbar.delete')}</span>
            </button>
          </div>

          <div className="toolbar-group">
            <button
              className="toolbar-button icon-only"
              onClick={zoomOut}
              title={t('tooltips.zoomOut')}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <circle cx="11" cy="11" r="8"></circle>
                <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                <line x1="8" y1="11" x2="14" y2="11"></line>
              </svg>
            </button>
            <div className="zoom-input-row">
              <input
                type="number"
                className="zoom-input"
                value={zoomInput}
                onChange={handleZoomInputChange}
                onBlur={handleZoomInputBlur}
                onKeyPress={handleZoomInputKeyPress}
                min="100"
                max="300"
                title="Zoom (100–300%)"
              />
              <span className="zoom-percent">%</span>
            </div>
            <button
              className="toolbar-button icon-only"
              onClick={zoomIn}
              title={t('tooltips.zoomIn')}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <circle cx="11" cy="11" r="8"></circle>
                <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                <line x1="11" y1="8" x2="11" y2="14"></line>
                <line x1="8" y1="11" x2="14" y2="11"></line>
              </svg>
            </button>
          </div>

          <div className="toolbar-group">
            <div className="pagination-arrows">
              <button
                className="toolbar-button icon-only"
                onClick={previousPage}
                disabled={pageNum <= 1}
                title={t('tooltips.previousPage')}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <polyline points="15 18 9 12 15 6"></polyline>
                </svg>
              </button>
              <button
                className="toolbar-button icon-only"
                onClick={nextPage}
                disabled={pageNum >= totalPages}
                title={t('tooltips.nextPage')}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <polyline points="9 18 15 12 9 6"></polyline>
                </svg>
              </button>
            </div>
            <div className="pagination-input-col">
              <input
                type="number"
                className="page-input"
                value={pageInput}
                onChange={handlePageInputChange}
                onBlur={handlePageInputBlur}
                onKeyPress={handlePageInputKeyPress}
                min="1"
                max={totalPages}
                title={t('page.enterPage', { total: totalPages })}
              />
              <span className="page-separator">{t('page.of')}</span>
              <span className="page-total">{totalPages}</span>
            </div>
          </div>

          <div className="toolbar-group">
            <button
              className="toolbar-button"
              onClick={toggleInstructions}
              title={t('tooltips.help')}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <circle cx="12" cy="12" r="10"></circle>
                <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"></path>
                <line x1="12" y1="17" x2="12.01" y2="17"></line>
              </svg>
              <span>{t('toolbar.help')}</span>
            </button>

            <button
              className="toolbar-button"
              onClick={saveChangesToSession}
              disabled={Object.keys(redactionBoxes).length === 0}
              title={t('tooltips.save')}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path>
                <polyline points="17 21 17 13 7 13 7 21"></polyline>
                <polyline points="7 3 7 8 15 8"></polyline>
              </svg>
              <span>{t('toolbar.save')}</span>
            </button>

            <div className="dropdown-container">
              {showRedactionModeDropdown && (
                <div className={`redaction-mode-dropdown ${dropdownPos}`}>
                  <button
                    className="dropdown-option"
                    onClick={() => generateFinalPDF('black')}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                      <rect x="3" y="3" width="18" height="18" rx="2" />
                    </svg>
                    <span>{t('redactionModes.black')}</span>
                  </button>
                  <button
                    className="dropdown-option"
                    onClick={() => generateFinalPDF('transparent')}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeDasharray="4 2">
                      <rect x="3" y="3" width="18" height="18" rx="2" />
                    </svg>
                    <span>{t('redactionModes.transparent')}</span>
                  </button>
                </div>
              )}
              <button
                className="toolbar-button primary"
                onClick={() => setShowRedactionModeDropdown(!showRedactionModeDropdown)}
                disabled={!hasBoxes}
                title={hasBoxes ? t('tooltips.generatePdf') : t('tooltips.addBoxesFirst')}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                  <polyline points="7 10 12 15 17 10"></polyline>
                  <line x1="12" y1="15" x2="12" y2="3"></line>
                </svg>
                <span>{t('toolbar.generatePdf')}</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <NotificationSystem
        notifications={notifications}
        onRemove={removeNotification}
      />
    </div>
  );
};

export default PDFEditor;
